import { Drawer } from '@dynamika-shared/ui/Drawer/Drawer';
import { DrawerProps, Tag } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { Button } from '@dynamika-shared/ui/Button/Button';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { CloseOutlined } from '@ant-design/icons';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useParams } from 'react-router-dom';
import {
  CreateTeamMemberConfig,
  EditTeamMemberConfig,
  Feature,
  TeamMember,
} from '@dynamika-shared/types/teamMember';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { twJoin } from '@utils/twMerge';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import { debounce } from 'lodash';
import { OptionRenderer } from '@components/OptionRenderer/OptionRenderer';
import { Option } from '@utils/generateOptions';
import { SelectedItems } from '@components/SelectedItems/SelectedItems';
import { filteredSupportedUiRoles, TeamRoles } from 'src/constants/rolesList';
import { OptionMembersRenderer } from './OptionMembersRenderer';
import { Attention } from '@dynamika-shared/ui/icons';
import { UnsavedChangesModal } from '@components/modals/UnsavedChangesModal/UnsavedChangesModal';
import { FlattenOptionData } from 'rc-select/lib/interface';
import { BaseOptionType } from 'rc-select/lib/Select';
import { useClinicalTrialSlice } from '@restore/clinicalTrial/clinicalTrial.slice';
import { useAffiliationUsersListSlice } from '@restore/affiliationUsersList/affiliationUsersList.slice';

type AddEditMemberProps = DrawerProps & {
  open: boolean;
  close?: () => void;
  updateTeam?: () => void;
  currentTeamMember?: TeamMember;
  editMode?: boolean;
};

export const AddEditMember: FC<AddEditMemberProps> = ({
  open,
  close,
  updateTeam,
  currentTeamMember,
  editMode,
}) => {
  const { id: clinicalTrialId } = useParams<{
    id: string;
  }>();
  const [askQuit, setAskQuit] = useState<boolean>(false);
  const [searchUserValue, setSearchUserValue] = useState<string>('');
  const [searchSiteValue, setSearchSiteValue] = useState<string>('');
  const sites = useClinicalTrialSlice(state => state.data?.sites ?? []);
  const [
    fetchUsers,
    users,
    totalUsers,
    usersLoading,
    addMemberStatus,
    editMemberStatus,
    addMember,
    editMember,
  ] = useAffiliationUsersListSlice((state, actions) => [
    actions.fetchAffiliationUsersList,
    state.data,
    state.totalUsers,
    state.loading,
    state.addMember,
    state.editMember,
    actions.addMemberRequest,
    actions.editMemberRequest,
  ]);

  const formConfig = editMode
    ? yup.object().shape({
        userId: yup.string().required(),
        features: yup
          .array()
          .of(yup.string().oneOf(Object.values(Feature)).required())
          .required(),
      })
    : yup.object().shape({
        userId: yup.string().required(),
        role: yup.string().required().oneOf(Object.values(TeamRoles)),
        siteIds: yup.array().required(),
        features: yup
          .array()
          .of(yup.string().oneOf(Object.values(Feature)).required())
          .required(),
      });

  const { setValue, getValues, trigger, watch, formState, reset } = useForm<
    CreateTeamMemberConfig | EditTeamMemberConfig
  >({
    resolver: yupResolver(formConfig),
  });

  const { isValid, isSubmitting, isDirty } = formState;
  const [selectedSites, setSelectedSites] = useState<Option[]>([]);
  const [viewOnlyAccessChecked, setViewOnlyAccessChecked] =
    useState<boolean>(false);
  const [viewLabeledSeriesChecked, setViewLabeledSeriesChecked] =
    useState<boolean>(false);
  const role = watch('role');

  const isFormDisabled =
    !isValid ||
    isSubmitting ||
    !isDirty ||
    (role === TeamRoles.SITE ? !selectedSites.length : false);

  const imagingFacilitiesIds = useMemo(() => {
    return new Set(
      sites.flatMap(site => site.imagingFacilities.map(facility => facility.id))
    );
  }, [sites]);

  const sitesOptions = useMemo(() => {
    const userFacility = users.find(u => u.id === getValues().userId)?.facility;
    if (!userFacility) return [];
    return sites
      .filter(s =>
        searchSiteValue
          ? s?.code?.toLowerCase()?.includes(searchSiteValue?.toLowerCase())
          : true
      )
      .reduce((acc: Option[], { code, id, imagingFacilities }) => {
        if (imagingFacilities.find(f => f.id === userFacility)) {
          acc.push({
            label: code,
            value: id as string,
          });
        }
        return acc;
      }, []);
  }, [getValues, sites, users, watch('userId'), searchSiteValue]);

  const usersOptions = useMemo(() => {
    if (editMode) {
      return [
        {
          label: currentTeamMember?.fullName,
          value: currentTeamMember?.id,
          disabled: true,
        },
      ] as unknown as Option[];
    }
    return users.map(user => ({
      label: user.fullName,
      value: user.id,
      disabled:
        role !== 'SITE' ? false : !imagingFacilitiesIds.has(user.facility),
    }));
  }, [editMode, users, role, imagingFacilitiesIds]);

  const submit = () => {
    if (clinicalTrialId) {
      if (editMode) {
        editMember({
          clinicalTrialId,
          ...(getValues() as EditTeamMemberConfig),
        });
      } else {
        addMember({
          clinicalTrialId,
          ...(getValues() as CreateTeamMemberConfig),
        });
      }
      setSelectedSites([]);
      setViewOnlyAccessChecked(false);
      setViewLabeledSeriesChecked(false);
      reset();
    }
  };

  useEffect(() => {
    if (addMemberStatus === 'SUCCESS' || editMemberStatus === 'SUCCESS') {
      updateTeam && updateTeam();
      close && close();
    }
  }, [addMemberStatus, editMemberStatus]);

  useEffect(() => {
    setValue('features', currentTeamMember?.features || []);
    setViewOnlyAccessChecked(
      currentTeamMember?.features?.includes(
        Feature.FEATURE_VIEW_SCORING_TASK
      ) || false
    );
    setViewLabeledSeriesChecked(
      currentTeamMember?.features?.includes(
        Feature.FEATURE_VIEW_LABELED_SERIES
      ) || false
    );
    setValue('userId', currentTeamMember?.id as string);
    setValue('role', currentTeamMember?.roles as TeamRoles);
  }, [currentTeamMember]);

  const onCloseDrawer = () => {
    if (!isDirty) {
      setSelectedSites([]);
      setViewOnlyAccessChecked(false);
      setViewLabeledSeriesChecked(false);
      reset();
      close && close();
    } else {
      setAskQuit(true);
    }
  };

  const onChangeItems = (items: Option[]) => {
    const newItems = items.map(label => label.value);
    setValue('siteIds', newItems, {
      shouldDirty: true,
      shouldTouch: true,
    });
    trigger('siteIds');
  };

  const handleTagClose = (removedItem: Option) => {
    const updatedItems = selectedSites.filter(item => item !== removedItem);
    setSelectedSites(updatedItems);
    onChangeItems(updatedItems);
  };

  const onSelect = (value: string) => {
    const selectedItem = sitesOptions.find(f => f.value === value);
    if (selectedItem) {
      setSelectedSites(prevState => {
        const updatedItems = [...prevState, selectedItem];
        onChangeItems(updatedItems);
        return [...prevState, selectedItem];
      });
    }
  };

  const onDeselect = (value: string) => {
    const indexToRemove = selectedSites.findIndex(item => item.value === value);
    if (indexToRemove !== -1) {
      setSelectedSites(prevState => {
        const updatedItems = [...prevState];
        updatedItems.splice(indexToRemove, 1);
        onChangeItems(updatedItems);
        return updatedItems;
      });
    }
  };

  const delayedSearch = useMemo(
    () =>
      debounce((name?: string) => {
        const values = getValues();
        const role = 'role' in values ? values.role : undefined;
        if (clinicalTrialId && role) {
          return fetchUsers({
            clinicalTrialId,
            role,
            filters: name,
          });
        }
      }, 500),
    [getValues, clinicalTrialId]
  );

  const toggleFeature = (feature: Feature, checked: boolean) => {
    const current = (getValues()?.features as Feature[]) || [];

    const next = checked
      ? current.filter(f => f !== feature)
      : [...current, feature];

    setValue('features', next, {
      shouldDirty: true,
      shouldTouch: true,
    });

    trigger('features');
  };

  const canViewLabeledSeries = role !== TeamRoles.SUPPORT;

  const allowedRoles = [
    TeamRoles.SPONSOR,
    TeamRoles.PROJECT_MANAGER,
    TeamRoles.SUPPORT,
  ];

  useEffect(() => {
    const values = getValues();
    const role = 'role' in values ? values.role : undefined;
    if (clinicalTrialId && role) {
      fetchUsers({ clinicalTrialId: clinicalTrialId, role });
    }
  }, [role, clinicalTrialId]);

  const membersOptionRenderer = (option: FlattenOptionData<BaseOptionType>) => (
    <OptionMembersRenderer
      searchValue={searchUserValue}
      option={option}
      userMember={users.find(u => u.id === option.data.value)}
      disabled={option.data.disabled}
      selectedOptions={usersOptions.filter(u => u.value === watch('userId'))}
    />
  );

  const optionRenderer = (option: FlattenOptionData<BaseOptionType>) => (
    <OptionRenderer
      searchValue={searchSiteValue}
      option={option}
      selectedOptions={selectedSites}
    />
  );

  const tagRenderer = () => <Tag className='hidden' />;

  let notFoundContent: null | JSX.Element = null;

  if (totalUsers > 0 && searchUserValue) {
    notFoundContent = (
      <NoDataSelect text={searchUserValue} maxLength={10} name={'users'} />
    );
  }

  if (totalUsers === 0 && !usersLoading) {
    notFoundContent = <NoDataSelect maxLength={10} name={'users'} />;
  }

  return (
    <Drawer
      classNames={{
        header:
          '!px-0 !pb-3 !pt-1 [&_button]:absolute [&_button]:top-[1.3rem] [&_button]:right-[0.4rem]',
        body: 'flex flex-col !px-0 !pb-0',
        content: 'rounded-lg',
      }}
      title={
        editMode ? `Edit Member ${currentTeamMember?.fullName}` : 'Add Member'
      }
      open={open}
      width={464}
      placement='right'
      onClose={onCloseDrawer}
      closeIcon={<CloseOutlined />}
      data-testid={'addMemberDrawer'}
      destroyOnHidden
    >
      <form className='overflow-auto px-1'>
        <Select
          listHeight={160}
          options={filteredSupportedUiRoles}
          placeholder={'Role Group'}
          disabled={editMode}
          onChange={value => {
            if (value !== TeamRoles.SITE) {
              setSelectedSites([]);
              setValue('siteIds', []);
              trigger('siteIds');
            }
            setValue('userId', '', { shouldDirty: true, shouldTouch: true });
            trigger('userId');
            setValue('role', value, { shouldDirty: true, shouldTouch: true });
            trigger('role');
            setViewOnlyAccessChecked(false);
            setViewLabeledSeriesChecked(false);
            setValue('features', [], { shouldDirty: true, shouldTouch: true });
            trigger('features');
          }}
          value={watch('role')}
          label={'Select Role Group'}
          required
          data-testid='role-member'
          className={twJoin('mb-3')}
        />

        {allowedRoles.includes(role) && (
          <div className='flex flex-col gap-2 mb-2'>
            {canViewLabeledSeries && (
              <label className='flex items-center gap-1 whitespace-nowrap ms-3'>
                <Checkbox
                  checked={viewLabeledSeriesChecked}
                  onChange={() => {
                    setViewLabeledSeriesChecked(prev => !prev);
                    toggleFeature(
                      Feature.FEATURE_VIEW_LABELED_SERIES,
                      viewLabeledSeriesChecked
                    );
                  }}
                />
                <span className='font-medium text-1424'>
                  Uploaded Data View Only Access
                </span>
              </label>
            )}

            <label className='flex items-center gap-1 whitespace-nowrap ms-3'>
              <Checkbox
                checked={viewOnlyAccessChecked}
                onChange={() => {
                  setViewOnlyAccessChecked(prev => !prev);
                  toggleFeature(
                    Feature.FEATURE_VIEW_SCORING_TASK,
                    viewOnlyAccessChecked
                  );
                }}
              />
              <span className='font-medium text-1424'>
                Completed Readings View Only Access
              </span>
            </label>
          </div>
        )}

        <Select
          showSearch
          searchValue={searchUserValue}
          onSearch={value => {
            setSearchUserValue(value);
            delayedSearch(value);
          }}
          placeholder={
            role === TeamRoles.SITE && !sites.length
              ? 'At least 1 site addition is required'
              : 'Select a User'
          }
          filterOption={false}
          listHeight={160}
          options={usersOptions}
          onChange={value => {
            setValue('userId', value, { shouldDirty: true, shouldTouch: true });
            trigger('userId');
            if (
              !editMode &&
              (getValues() as CreateTeamMemberConfig).siteIds?.length
            ) {
              setSelectedSites([]);
              onChangeItems([]);
            }
          }}
          disabled={
            !watch('role') ||
            (role === TeamRoles.SITE && !sites.length) ||
            editMode
          }
          optionRender={membersOptionRenderer}
          notFoundContent={notFoundContent}
          value={watch('userId')}
          label={'Select a User'}
          required
          data-testid='user-member'
          className={twJoin('mb-3')}
        />

        {!editMode && role === TeamRoles.SITE && sites.length === 0 && (
          <div className='border-honoluluBlue border-[1px] border-opacity-40 flex items-center bg-aliceBlue p-1.5 rounded-2xl'>
            <Attention className='text-honoluluBlue mr-1' />
            <p>There are no sites added. Please add sites first.</p>
          </div>
        )}

        {!editMode && role === TeamRoles.SITE && watch('userId') && (
          <>
            <Select
              listHeight={160}
              mode='multiple'
              options={sitesOptions}
              onChange={value => {
                if (!editMode) {
                  const newSites = [
                    ...((getValues() as CreateTeamMemberConfig)?.siteIds || []),
                    value,
                  ];
                  setValue('siteIds', newSites);
                  trigger('siteIds');
                }
              }}
              onSelect={onSelect}
              onDeselect={onDeselect}
              onBlur={() => setSearchSiteValue('')}
              tagRender={tagRenderer}
              menuItemSelectedIcon={null}
              optionRender={optionRenderer}
              filterOption={false}
              searchValue={searchSiteValue}
              onSearch={value => {
                setSearchSiteValue(value);
              }}
              value={watch('siteIds')}
              label={'Select a Site'}
              required
              data-testid='site-member'
              disabled={editMode}
            />
            <SelectedItems
              selectedItems={selectedSites}
              onClose={handleTagClose}
              title={'Selected Sites'}
              className={twJoin('mb-3')}
            />
          </>
        )}

      </form>
      <footer className='mt-auto'>
        <hr className='mb-3' />
        <div className='flex justify-between'>
          <Button className='w-[17rem]' onClick={onCloseDrawer}>
            Cancel
          </Button>
          <Button
            className='w-[17rem]'
            type='primary'
            disabled={isFormDisabled || addMemberStatus === 'LOADING'}
            onClick={submit}
          >
            {editMode ? 'Save' : 'Add'}
          </Button>
        </div>
      </footer>
      <UnsavedChangesModal
        onCancel={() => setAskQuit(false)}
        onConfirm={() => {
          setSelectedSites([]);
          reset();
          setAskQuit(false);
          close && close();
        }}
        open={askQuit}
      />
    </Drawer>
  );
};
